/* =====================================================================
   Mawlaii Wishlist — local-only, no account sync.
   Stores an array of product handles in localStorage under 'mawlaii:wishlist'.
   Repaints every [data-wishlist-toggle] button on load + every change.
   Updates header count badge ([data-wishlist-count]) on load + change.
   Renders the /pages/wishlist grid client-side from /products/HANDLE.js.
   ===================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'mawlaii:wishlist';
  var EVENT_CHANGED = 'wishlist:changed';

  // ---------- store ----------
  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(function (h) { return typeof h === 'string' && h.length > 0; }) : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) { /* quota or disabled */ }
    document.dispatchEvent(new CustomEvent(EVENT_CHANGED, { detail: { list: list.slice() } }));
  }

  var Wishlist = {
    all: function () { return read(); },
    has: function (handle) { return read().indexOf(handle) !== -1; },
    count: function () { return read().length; },
    add: function (handle) {
      var list = read();
      if (list.indexOf(handle) === -1) { list.push(handle); write(list); }
    },
    remove: function (handle) {
      var list = read();
      var i = list.indexOf(handle);
      if (i !== -1) { list.splice(i, 1); write(list); }
    },
    toggle: function (handle) {
      if (this.has(handle)) this.remove(handle);
      else this.add(handle);
    },
    clear: function () { write([]); }
  };

  window.MawlaiiWishlist = Wishlist;

  // ---------- button paint ----------
  function paintButtons() {
    var list = read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      var active = list.indexOf(handle) !== -1;
      btn.classList.toggle('wishlist-btn--active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Save to wishlist');
      var label = btn.querySelector('.wishlist-btn__label');
      if (label) label.textContent = active ? 'Saved' : 'Save';
    });
  }

  // ---------- header count ----------
  function paintCount() {
    var n = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n > 0 ? '(' + n + ')' : '';
    });
  }

  // ---------- click handler ----------
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    Wishlist.toggle(handle);
  });

  document.addEventListener(EVENT_CHANGED, function () {
    paintButtons();
    paintCount();
    if (window.__wishlistPageRender) window.__wishlistPageRender();
  });

  // sync across tabs
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) {
      paintButtons();
      paintCount();
      if (window.__wishlistPageRender) window.__wishlistPageRender();
    }
  });

  // initial paint
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { paintButtons(); paintCount(); });
  } else {
    paintButtons();
    paintCount();
  }

  // ---------- wishlist page renderer ----------
  // Looks for a #WishlistGrid element on the page, then fetches each saved
  // product's JSON and renders cards. Re-runs on every change.
  function formatMoney(cents) {
    var rupees = Math.round(cents) / 100;
    var formatted = rupees.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return 'Rs. ' + formatted;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  async function fetchProduct(handle) {
    try {
      var res = await fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) { return null; }
  }

  function renderEmpty(target) {
    target.innerHTML = '' +
      '<div class="wishlist-empty">' +
      '  <p class="wishlist-empty__eyebrow">Nothing kept yet.</p>' +
      '  <p class="wishlist-empty__line">Tap the heart on anything you’d like to set aside &mdash; it will live here, on this device, until you decide.</p>' +
      '  <a href="/collections" class="wishlist-empty__cta">Browse the Maison <span aria-hidden="true">&rarr;</span></a>' +
      '</div>';
  }

  function renderCard(p) {
    if (!p) return '';
    var img = (p.featured_image || (p.images && p.images[0])) || '';
    var url = p.url || ('/products/' + p.handle);
    var firstVariantId = (p.variants && p.variants[0] && p.variants[0].id) || null;
    var available = p.available !== false;
    return '' +
      '<article class="wishlist-card" data-handle="' + escapeHtml(p.handle) + '">' +
      '  <a class="wishlist-card__media" href="' + escapeHtml(url) + '" aria-label="' + escapeHtml(p.title) + '">' +
      (img ? '    <img src="' + escapeHtml(img) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" />' : '<div class="wishlist-card__placeholder"></div>') +
      '  </a>' +
      '  <div class="wishlist-card__body">' +
      '    <p class="wishlist-card__vendor">' + escapeHtml(p.vendor || '') + '</p>' +
      '    <h3 class="wishlist-card__title"><a href="' + escapeHtml(url) + '">' + escapeHtml(p.title) + '</a></h3>' +
      '    <p class="wishlist-card__price">' + formatMoney(p.price) + '</p>' +
      '    <div class="wishlist-card__actions">' +
      (available && firstVariantId
        ? '<form action="/cart/add" method="post" enctype="multipart/form-data" data-wishlist-add>' +
          '  <input type="hidden" name="id" value="' + firstVariantId + '" />' +
          '  <button type="submit" class="wishlist-card__add">Add to bag</button>' +
          '</form>'
        : '<span class="wishlist-card__soldout">Sold out</span>') +
      '      <button type="button" class="wishlist-card__remove" data-wishlist-remove="' + escapeHtml(p.handle) + '" aria-label="Remove ' + escapeHtml(p.title) + ' from wishlist">Remove</button>' +
      '    </div>' +
      '  </div>' +
      '</article>';
  }

  async function renderPage() {
    var grid = document.getElementById('WishlistGrid');
    if (!grid) return;
    var status = document.getElementById('WishlistStatus');
    var list = read();
    if (list.length === 0) {
      if (status) status.textContent = '';
      renderEmpty(grid);
      return;
    }
    if (status) status.textContent = list.length === 1 ? '1 item kept.' : list.length + ' items kept.';
    grid.innerHTML = '<div class="wishlist-grid__loading">Loading your saved items&hellip;</div>';
    var products = await Promise.all(list.map(fetchProduct));
    // drop any unfound (deleted) products from local store
    var valid = [];
    products.forEach(function (p, i) { if (p) valid.push(list[i]); });
    if (valid.length !== list.length) write(valid);
    var html = products.filter(Boolean).map(renderCard).join('');
    grid.innerHTML = html || '';
    if (products.filter(Boolean).length === 0) renderEmpty(grid);
  }

  // Remove button + add-to-cart handlers, delegated
  document.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-wishlist-remove]');
    if (rm) {
      e.preventDefault();
      Wishlist.remove(rm.getAttribute('data-wishlist-remove'));
    }
  });

  // expose for cross-call refreshing
  window.__wishlistPageRender = renderPage;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderPage);
  } else {
    renderPage();
  }
})();
